/**
 * Startup restore rules (useBootstrapAuth) and the per-tab "signed out on
 * purpose" flag. Free of React and the store so they are unit tested
 * directly.
 */

/** sessionStorage, so the flag is per tab and survives the reload that
 *  follows a sign-out, but not a new tab or window. */
export const EXPLICIT_SIGN_OUT_KEY = 'evi-admin-signed-out';

type FlagStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function tabStorage(): FlagStorage | null {
  // Reading sessionStorage itself can throw when site data is blocked.
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Set before the logout request, so a request that fails (and leaves the
 *  refresh cookie in place) cannot have the next startup quietly restore
 *  the session the person just ended. */
export function markExplicitSignOut(storage: FlagStorage | null = tabStorage()): void {
  try { storage?.setItem(EXPLICIT_SIGN_OUT_KEY, '1'); } catch { /* storage blocked */ }
}

/** Cleared on every sign-in: the sign-out is no longer the latest word. */
export function clearExplicitSignOut(storage: FlagStorage | null = tabStorage()): void {
  try { storage?.removeItem(EXPLICIT_SIGN_OUT_KEY); } catch { /* storage blocked */ }
}

export function wasExplicitlySignedOut(storage: FlagStorage | null = tabStorage()): boolean {
  try { return storage?.getItem(EXPLICIT_SIGN_OUT_KEY) === '1'; } catch { return false; }
}

/** The routes App.tsx renders above its auth gate. They run their own
 *  sign-in (invite accept, magic-link verify, password reset, MFA), so a
 *  restore racing them could overwrite the session they set. Keep in step
 *  with those early returns. */
const PRE_AUTH_ROUTES = new Set([
  '/sales-partner-invite',
  '/auth/magic-verify',
  '/auth/reset',
  '/auth/mfa/enroll',
  '/auth/mfa/challenge',
]);

export function isPreAuthRoute(pathname: string): boolean {
  return PRE_AUTH_ROUTES.has(pathname);
}

export function shouldRestoreAtStartup(state: {
  isAuthenticated: boolean;
  alreadyAttempted: boolean;
  signedOutOnPurpose: boolean;
  pathname: string;
}): boolean {
  return !state.isAuthenticated
    && !state.alreadyAttempted
    && !state.signedOutOnPurpose
    && !isPreAuthRoute(state.pathname);
}
